<script setup>
// S16 C6 — provider-neutral catalogue scope and readiness preview.
// Provider identifiers may be used as opaque selection values but are never
// rendered. Labels, counts, cost and recovery truth are server-authored.
import { computed, onBeforeMount, ref, watch } from "vue";
import { useCompanyStore } from "@/stores/company";
import { useShopifyStore } from "@/stores/shopify";

const props = defineProps({
  integrationId: { type: String, required: true },
  projectionSettings: { type: Object, default: () => ({}) },
});
const emit = defineEmits(["ready"]);

const shopifyStore = useShopifyStore();
const companyId = useCompanyStore().companyId;
const RESOURCE_TYPES = ["manual", "installation", "specification", "safety", "warranty"];
const DEFAULT_RESOURCE_POLICY = Object.freeze({
  enabled: false,
  selected_types: ["manual", "installation", "specification", "safety", "warranty"],
  max_files: 100,
  max_bytes: 52428800,
  max_pages: 5000,
  max_concurrency: 1,
  per_file_max_bytes: 10485760,
  per_file_max_pages: 500,
});

const publishedOnly = ref(true);
const selectedCollectionIds = ref([]);
const featuredCollectionOrder = ref([]);
const resourcePolicy = ref({ ...DEFAULT_RESOURCE_POLICY });
const savedScope = ref(null);
const preview = ref(null);
const loadingPreview = ref(false);
const previewError = ref("");

function normalizePolicy(policy = {}) {
  return {
    ...DEFAULT_RESOURCE_POLICY,
    ...policy,
    selected_types: [...(policy.selected_types || DEFAULT_RESOURCE_POLICY.selected_types)],
  };
}

function normalizeScope(settings = {}) {
  const selected = [...(settings.selected_collection_ids || [])];
  const featured = [...(settings.featured_collection_order || [])]
    .filter((id) => selected.includes(id));
  return {
    published_only: settings.published_only ?? true,
    selected_collection_ids: selected,
    featured_collection_order: featured,
    resource_policy: normalizePolicy(settings.resource_policy),
  };
}

function scopePayload() {
  return {
    published_only: publishedOnly.value,
    selected_collection_ids: [...selectedCollectionIds.value],
    featured_collection_order: featuredCollectionOrder.value
      .filter((id) => selectedCollectionIds.value.includes(id)),
    resource_policy: {
      ...resourcePolicy.value,
      selected_types: [...resourcePolicy.value.selected_types],
    },
  };
}

function loadSavedScope(settings) {
  const scope = normalizeScope(settings);
  savedScope.value = scope;
  publishedOnly.value = scope.published_only;
  selectedCollectionIds.value = [...scope.selected_collection_ids];
  featuredCollectionOrder.value = [...scope.featured_collection_order];
  resourcePolicy.value = normalizePolicy(scope.resource_policy);
}

watch(() => props.projectionSettings, loadSavedScope, { immediate: true, deep: true });

const scopeChanged = computed(
  () => JSON.stringify(scopePayload()) !== JSON.stringify(savedScope.value)
);
const collections = computed(() => Array.isArray(preview.value?.collections) ? preview.value.collections : []);
const recommendationPreview = computed(
  () => Array.isArray(preview.value?.recommendations) ? preview.value.recommendations : []
);

function collectionKey(collection) {
  return collection?.selection_key || collection?.id || "";
}

function collectionLabel(collection) {
  return collection?.display_name || collection?.title || collection?.name || "Unnamed collection";
}

function toggleCollection(id, selected) {
  if (!id) return;
  if (selected && !selectedCollectionIds.value.includes(id)) {
    selectedCollectionIds.value.push(id);
  } else if (!selected) {
    selectedCollectionIds.value = selectedCollectionIds.value.filter((value) => value !== id);
    featuredCollectionOrder.value = featuredCollectionOrder.value.filter((value) => value !== id);
  }
}

function toggleFeatured(id, featured) {
  if (!selectedCollectionIds.value.includes(id)) return;
  if (featured && !featuredCollectionOrder.value.includes(id)) {
    featuredCollectionOrder.value.push(id);
  } else if (!featured) {
    featuredCollectionOrder.value = featuredCollectionOrder.value.filter((value) => value !== id);
  }
}

function moveFeatured(id, offset) {
  const current = featuredCollectionOrder.value.indexOf(id);
  const destination = current + offset;
  if (current < 0 || destination < 0 || destination >= featuredCollectionOrder.value.length) return;
  const next = [...featuredCollectionOrder.value];
  [next[current], next[destination]] = [next[destination], next[current]];
  featuredCollectionOrder.value = next;
}

function toggleResourceType(type, enabled) {
  const current = new Set(resourcePolicy.value.selected_types);
  enabled ? current.add(type) : current.delete(type);
  resourcePolicy.value.selected_types = RESOURCE_TYPES.filter((item) => current.has(item));
}

function warningText(warning) {
  return typeof warning === "string" ? warning : warning?.message || warning?.code || "Catalogue warning";
}

async function runPreview() {
  loadingPreview.value = true;
  previewError.value = "";
  try {
    preview.value = await shopifyStore.fetchPreview(companyId, props.integrationId);
    emit("ready", preview.value);
  } catch (error) {
    previewError.value = error?.data?.message || "Could not generate preview.";
  } finally {
    loadingPreview.value = false;
  }
}

async function saveScope() {
  loadingPreview.value = true;
  previewError.value = "";
  try {
    const scope = scopePayload();
    await shopifyStore.updateProjectionSettings(companyId, props.integrationId, scope);
    savedScope.value = normalizeScope(scope);
    await runPreview();
  } catch (error) {
    previewError.value = error?.data?.message || "Could not save catalogue scope.";
  } finally {
    loadingPreview.value = false;
  }
}

onBeforeMount(runPreview);

defineExpose({ preview, runPreview, saveScope, scopePayload, scopeChanged });
</script>

<template>
  <div class="scope-step">
    <h3>Catalogue scope and preview</h3>
    <p class="scope-explain">
      Choose browse shelves and processing policy. Product search remains Site-scoped and provider-neutral.
    </p>

    <label class="scope-toggle">
      <input type="checkbox" v-model="publishedOnly" data-test="published-only" />
      Only active and published products
    </label>

    <section v-if="collections.length" class="scope-section" data-test="collection-selection">
      <h4>Browse shelves</h4>
      <p>Select collections to publish. Featured shelves appear in the order shown.</p>
      <div v-for="collection in collections" :key="collectionKey(collection)" class="collection-row">
        <label>
          <input
            type="checkbox"
            data-test="collection-select"
            :checked="selectedCollectionIds.includes(collectionKey(collection))"
            @change="toggleCollection(collectionKey(collection), $event.target.checked)"
          />
          <strong>{{ collectionLabel(collection) }}</strong>
          <span v-if="collection.product_count !== undefined">{{ collection.product_count }} products</span>
        </label>
        <div v-if="selectedCollectionIds.includes(collectionKey(collection))" class="feature-controls">
          <label>
            <input
              type="checkbox"
              data-test="feature-collection"
              :checked="featuredCollectionOrder.includes(collectionKey(collection))"
              @change="toggleFeatured(collectionKey(collection), $event.target.checked)"
            />
            Feature on kiosk
          </label>
          <template v-if="featuredCollectionOrder.includes(collectionKey(collection))">
            <button type="button" aria-label="Move featured shelf earlier" @click="moveFeatured(collectionKey(collection), -1)">↑</button>
            <span>Order {{ featuredCollectionOrder.indexOf(collectionKey(collection)) + 1 }}</span>
            <button type="button" aria-label="Move featured shelf later" @click="moveFeatured(collectionKey(collection), 1)">↓</button>
          </template>
        </div>
      </div>
    </section>

    <section class="scope-section" data-test="resource-policy">
      <h4>Product resources</h4>
      <label class="scope-toggle">
        <input type="checkbox" v-model="resourcePolicy.enabled" data-test="resource-enabled" />
        Process selected manuals and product documents after catalogue publication
      </label>
      <template v-if="resourcePolicy.enabled">
        <div class="resource-types">
          <label v-for="type in RESOURCE_TYPES" :key="type">
            <input
              type="checkbox"
              :checked="resourcePolicy.selected_types.includes(type)"
              @change="toggleResourceType(type, $event.target.checked)"
            />
            {{ type }}
          </label>
        </div>
        <div class="budget-grid">
          <label>Files <input v-model.number="resourcePolicy.max_files" type="number" min="1" /></label>
          <label>Total MB <input :value="Math.round(resourcePolicy.max_bytes / 1048576)" type="number" min="1" @input="resourcePolicy.max_bytes = Number($event.target.value) * 1048576" /></label>
          <label>Pages <input v-model.number="resourcePolicy.max_pages" type="number" min="1" /></label>
          <label>Concurrent files <input v-model.number="resourcePolicy.max_concurrency" type="number" min="1" max="16" /></label>
        </div>
      </template>
    </section>

    <div class="scope-actions">
      <button type="button" class="btn btn-primary" @click="saveScope" :disabled="loadingPreview || !scopeChanged" data-test="btn-save-scope">
        Save catalogue settings
      </button>
      <button type="button" class="btn btn-secondary" @click="runPreview" :disabled="loadingPreview || scopeChanged" data-test="btn-preview">
        Refresh preview
      </button>
      <span v-if="loadingPreview" class="spinner-sm" role="status" aria-label="Loading catalogue preview"></span>
    </div>

    <p v-if="scopeChanged" class="scope-unsaved" data-test="scope-unsaved">
      Save settings before refreshing; the current preview still reflects the saved configuration.
    </p>
    <div v-if="previewError" class="shopify-alert" role="alert" data-test="preview-error">{{ previewError }}</div>

    <div v-if="preview" class="scope-preview" data-test="preview-panel">
      <div class="scope-metrics">
        <div class="scope-metric"><b>{{ preview.family_count ?? 0 }}</b><span>Product families</span></div>
        <div class="scope-metric"><b>{{ preview.sellable_variant_count ?? 0 }}</b><span>Sellable variants</span></div>
        <div class="scope-metric"><b>{{ preview.media_count ?? preview.image_count ?? 0 }}</b><span>Media items</span></div>
        <div class="scope-metric"><b>{{ preview.resource_count ?? 0 }}</b><span>Resources</span></div>
      </div>

      <div v-if="preview.taxonomy" class="preview-grid" data-test="taxonomy-preview">
        <div><b>{{ preview.taxonomy.standard_count ?? 0 }}</b><span>Standard taxonomy</span></div>
        <div><b>{{ preview.taxonomy.product_type_fallback_count ?? 0 }}</b><span>Product type fallback</span></div>
        <div><b>{{ preview.taxonomy.unclassified_count ?? 0 }}</b><span>Unclassified</span></div>
      </div>

      <div v-if="preview.resource_estimate" class="preview-card" data-test="resource-estimate">
        <h4>Estimated resource processing</h4>
        <p>
          {{ preview.resource_estimate.file_count ?? 0 }} files ·
          {{ preview.resource_estimate.page_count ?? 0 }} pages ·
          {{ preview.resource_estimate.display_cost || "Cost unavailable" }}
        </p>
        <p v-if="preview.resource_estimate.over_budget" class="scope-warning">The current selection exceeds its saved tenant budget.</p>
      </div>

      <div v-if="recommendationPreview.length" class="preview-card" data-test="recommendation-preview">
        <h4>Recommendation interpretation</h4>
        <div v-for="item in recommendationPreview" :key="item.plan_id || `${item.trigger}-${item.target}`" class="recommendation-row">
          <span>{{ item.trigger_label || item.trigger }}</span>
          <span aria-hidden="true">→</span>
          <span>{{ item.target_label || item.target }}</span>
          <strong>{{ item.status || (item.ambiguous ? "Needs review" : "Ready") }}</strong>
        </div>
      </div>

      <div v-if="preview.catalogue_state" class="preview-card" data-test="catalogue-state">
        <h4>Current shopper state</h4>
        <p>{{ preview.catalogue_state.summary }}</p>
        <p v-if="preview.catalogue_state.last_good_at">Last good catalogue: {{ preview.catalogue_state.last_good_at }}</p>
        <p v-if="preview.catalogue_state.next_action"><strong>Next safe action:</strong> {{ preview.catalogue_state.next_action }}</p>
      </div>

      <div v-if="preview.warnings?.length" class="scope-warnings" data-test="preview-warnings">
        <h4>Warnings</h4>
        <ul><li v-for="(warning, index) in preview.warnings" :key="index">{{ warningText(warning) }}</li></ul>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.scope-step { display: flex; flex-direction: column; gap: 12px; }
.scope-step h3, .scope-step h4, .scope-step p { margin: 0; }
.scope-explain { color: var(--neutral-600); font-size: var(--text-sm); }
.scope-toggle { display: flex; gap: 8px; align-items: center; }
.scope-section, .preview-card {
  border: 1px solid var(--neutral-200); border-radius: 10px; padding: 12px;
  display: flex; flex-direction: column; gap: 8px;
}
.collection-row { border-top: 1px solid var(--neutral-100); padding-top: 8px; }
.collection-row > label { display: flex; gap: 8px; align-items: center; }
.collection-row span { color: var(--neutral-500); font-size: var(--text-sm); }
.feature-controls { display: flex; gap: 8px; align-items: center; margin: 6px 0 0 24px; }
.feature-controls label { display: flex; gap: 6px; align-items: center; }
.feature-controls button { border: 1px solid var(--neutral-200); border-radius: 6px; background: transparent; }
.resource-types { display: flex; flex-wrap: wrap; gap: 12px; text-transform: capitalize; }
.resource-types label { display: flex; gap: 5px; }
.budget-grid { display: grid; grid-template-columns: repeat(4, minmax(110px, 1fr)); gap: 8px; }
.budget-grid label { display: flex; flex-direction: column; font-size: var(--text-sm); }
.budget-grid input { width: 100%; }
.scope-actions { display: flex; align-items: center; gap: 8px; }
.scope-unsaved, .scope-warning, .scope-warnings { color: var(--amber-700, #b45309); font-size: var(--text-sm); }
.shopify-alert { background: var(--red-50, #fef2f2); color: var(--red-700, #b91c1c); border: 1px solid var(--red-200, #fecaca); border-radius: 8px; padding: 8px 12px; }
.scope-metrics { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; }
.scope-metric, .preview-grid > div { border: 1px solid var(--neutral-200); border-radius: 10px; padding: 10px; text-align: center; }
.scope-metric b, .preview-grid b { display: block; font-size: 20px; font-variant-numeric: tabular-nums; }
.scope-metric span, .preview-grid span { color: var(--neutral-500); font-size: 11px; text-transform: uppercase; }
.preview-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; }
.recommendation-row { display: grid; grid-template-columns: 1fr auto 1fr auto; gap: 8px; align-items: center; }
@media (max-width: 800px) {
  .scope-metrics, .preview-grid, .budget-grid { grid-template-columns: repeat(2, 1fr); }
}
</style>
